import { useEffect, useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';

export default function CreateBookingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [bookingSource, setBookingSource] = useState('AGENCY');
  const [statusId, setStatusId] = useState('');
  const [clients, setClients] = useState([]);
  const [resources, setResources] = useState([]);
  const [editions, setEditions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [form, setForm] = useState({
    clientId: '',
    mediaResourceId: '',
    editionId: '',
    positionId: '',
    mediaType: 'PRINT',
    platformDetails: '',
    timeFrom: '',
    timeTo: '',
    materialDueDate: '',
    notes: '',
  });

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '0.8rem',
      padding: '0.1rem',
      borderColor: '#e0e5f1',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#1c4fe0'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#1c4fe0' : state.isFocused ? '#f0f4ff' : 'white',
      color: state.isSelected ? 'white' : '#0f172a',
      '&:active': {
        backgroundColor: '#1c4fe0'
      }
    })
  };
  const [releaseDates, setReleaseDates] = useState([{ date: '' }]);
  const [message, setMessage] = useState('');
  const [dateWarning, setDateWarning] = useState('');
  const [conflicts, setConflicts] = useState({ blockers: [], warnings: [] });
  const [editingBooking, setEditingBooking] = useState(null);
  const [editBookingData, setEditBookingData] = useState(null);
  const [editReleaseDates, setEditReleaseDates] = useState([]);
  const [newClientName, setNewClientName] = useState('');

  const validateDates = (materialDate, releases) => {
    if (!materialDate || !releases.length) {
      setDateWarning('');
      return;
    }
    const due = new Date(materialDate);
    const invalid = releases.some((rd) => {
      if (!rd.date) return false;
      const release = new Date(rd.date);
      return release <= due;
    });

    if (invalid) {
      setDateWarning('Material Due Date must be earlier than every Release Date');
    } else {
      setDateWarning('');
    }
  };

  const handleSubmit = async (override = false) => {
    // If override is an event object (from a direct button click), default it to false
    const shouldOverride = typeof override === 'boolean' ? override : false;

    const isPrint = form.mediaType === 'Print';
    const isPublication = bookingSource === 'PUBLICATION';
    
    const isRequiredFilled = (form.clientId || newClientName) && form.mediaResourceId && form.editionId && form.positionId && (isPublication || statusId);

    if (!isRequiredFilled || releaseDates.length === 0 || releaseDates.some((rd) => !rd.date)) {
      setMessage('Fill required fields before saving');
      return;
    }

    const bookedStatus = statuses.find(s => s.name === 'Booked');
    const finalStatusId = isPublication ? (bookedStatus ? bookedStatus.id : statusId) : statusId;

    const payload = {
      bookingSource,
      clientId: form.clientId,
      newClientName: newClientName,
      mediaResourceId: form.mediaResourceId,
      editionId: form.editionId,
      positionId: form.positionId,
      mediaType: form.mediaType, // Keep original case or handle in backend
      platformDetails: form.platformDetails,
      timeFrom: form.timeFrom,
      timeTo: form.timeTo,
      materialDueDate: form.materialDueDate,
      ownerUserId: null,
      statusId: finalStatusId,
      notes: form.notes,
      overrideConflicts: shouldOverride,
      releaseDates: releaseDates.map((rd) => ({
        releaseDate: rd.date,
        statusId: finalStatusId,
        isLocked: 0,
      })),
    };

    // Check if there are blockers - if so, prevent saving (unless admin override)
    if (conflicts.blockers.length > 0 && !shouldOverride) {
      if (isAdmin) {
        setMessage('Agency booking conflicts detected. Use "Override & Save" button if you need to proceed.');
      } else {
        setMessage('Cannot create booking: This position is already booked by our agency. Only admins can override.');
      }
      return;
    }
    
    // If admin override but user is not admin, prevent
    if (conflicts.blockers.length > 0 && shouldOverride && !isAdmin) {
      setMessage('Only administrators can override agency booking conflicts.');
      return;
    }

    try {
      setConflicts({ blockers: [], warnings: [] });
      await api.post('/bookings', payload);
      setMessage('Booking created successfully');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);

      setEditingBooking(null);
      setEditBookingData(null);
      setNewClientName('');
      setBookingSource('AGENCY');
      setForm(prev => ({
        ...prev,
        clientId: '',
        mediaResourceId: '',
        editionId: '',
        positionId: '',
        mediaType: 'PRINT',
        platformDetails: '',
        timeFrom: '',
        timeTo: '',
        materialDueDate: '',
        notes: '',
      }));
      setReleaseDates([{ date: '' }]);
      if (statuses.length > 0) {
        setStatusId(statuses[0].id);
      } else {
        setStatusId('');
      }
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.details) {
        setConflicts(error.response.data.details);
        setMessage('Conflicts detected. Please review below.');
      } else {
        const errorMsg = error.response?.data?.message || error.message || 'Unable to create booking';
        setMessage(`Error: ${errorMsg}`);
      }
    }
  };

  useEffect(() => {
    const checkConflicts = async () => {
      const isPrint = form.mediaType === 'Print';
      const isPublication = bookingSource === 'PUBLICATION';
      
      const isRequiredFilled = form.clientId && form.mediaResourceId && form.editionId && form.positionId && (isPublication || statusId);

      if (!isRequiredFilled || releaseDates.length === 0 || releaseDates.some((rd) => !rd.date)) {
        setConflicts({ blockers: [], warnings: [] });
        return;
      }

      const bookedStatus = statuses.find(s => s.name === 'Booked');
      const finalStatusId = isPublication ? (bookedStatus ? bookedStatus.id : statusId) : statusId;

      const payload = {
        bookingSource,
        clientId: form.clientId,
        mediaResourceId: form.mediaResourceId,
        editionId: form.editionId,
        positionId: form.positionId,
        mediaType: form.mediaType,
        releaseDates: releaseDates.map((rd) => ({
          releaseDate: rd.date,
          statusId: finalStatusId,
        })),
      };

      try {
        const response = await api.post('/bookings/check-conflicts', payload);
        setConflicts(response.data.details || { blockers: [], warnings: [] });
      } catch (error) {
        console.error('Error checking conflicts:', error);
      }
    };

    const timeoutId = setTimeout(checkConflicts, 500);
    return () => clearTimeout(timeoutId);
  }, [form.clientId, form.mediaResourceId, form.editionId, form.positionId, form.mediaType, releaseDates, bookingSource, statusId]);

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [clientsRes, resourcesRes, editionsRes, positionsRes, statusesRes] = await Promise.all([
          api.get('/master/clients'),
          api.get('/master/media-resources'),
          api.get('/master/editions'),
          api.get('/master/positions'),
          api.get('/statuses'),
        ]);
        setClients(clientsRes.data.clients);
        setResources(resourcesRes.data.resources);
        setEditions(editionsRes.data.editions);
        setPositions(positionsRes.data.positions);
        setStatuses(statusesRes.data.statuses);
        if (statusesRes.data.statuses.length) {
          setStatusId(statusesRes.data.statuses[0].id);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchMasters();
  }, []);

  const filteredResources = resources.filter(r => 
    r.media_type?.toUpperCase() === form.mediaType?.toUpperCase()
  );

  return (
    <div className="page-content">
      <div className="card create-booking-card">
        <div className="section source-section">
          <label className="section-label">Booking Source</label>
          <div className="source-buttons">
            {['Agency', 'Publication'].map((source) => (
              <button
                key={source}
                className={`source-button ${bookingSource === source.toUpperCase() ? 'active' : ''}`}
                onClick={() => setBookingSource(source.toUpperCase())}
              >
                <span className="dot" />
                <div>
                  <strong>{source} Booking</strong>
                  <div className="subtext">{source === 'Agency' ? 'Booking made by agency' : 'Direct publication booking'}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="section source-section">
          <label className="section-label">Media Type</label>
          <div className="media-type-row">
            {['Print', 'Radio', 'TV', 'Digital'].map((type) => (
              <button
                key={type}
                className={`media-pill ${form.mediaType === type ? 'active' : ''}`}
                onClick={() => setForm({ ...form, mediaType: type, mediaResourceId: '' })}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <section className="section form-grid">
          <div className="form-field">
            <label>Client Name</label>
            {bookingSource === 'PUBLICATION' ? (
              <CreatableSelect
                options={clients.map(c => ({ value: c.id, label: c.name }))}
                value={
                  form.clientId 
                    ? (clients.find(c => c.id === form.clientId) ? { value: form.clientId, label: clients.find(c => c.id === form.clientId).name } : null)
                    : newClientName 
                      ? { value: 'new', label: newClientName }
                      : null
                }
                onChange={(opt) => {
                  if (opt?.__isNew__) {
                    setNewClientName(opt.label);
                    setForm({ ...form, clientId: '' });
                  } else {
                    setNewClientName('');
                    setForm({ ...form, clientId: opt ? opt.value : '' });
                  }
                }}
                placeholder="Select or type new client..."
                styles={customSelectStyles}
                isSearchable
                isClearable
              />
            ) : (
            <Select
              options={clients.map(c => ({ value: c.id, label: c.name }))}
              value={form.clientId ? { value: form.clientId, label: clients.find(c => c.id === form.clientId)?.name || '' } : null}
              onChange={(opt) => setForm({ ...form, clientId: opt ? opt.value : '' })}
              placeholder="Search client..."
              styles={customSelectStyles}
              isSearchable
              isClearable
            />
            )}
          </div>
          <div className="form-field">
            <label>Publication</label>
            <Select
              options={filteredResources.map(r => ({ value: r.id, label: r.name }))}
              value={form.mediaResourceId ? { value: form.mediaResourceId, label: resources.find(r => r.id === form.mediaResourceId)?.name || '' } : null}
              onChange={(opt) => setForm({ ...form, mediaResourceId: opt ? opt.value : '' })}
              placeholder="Search publication..."
              styles={customSelectStyles}
              isSearchable
              isClearable
            />
          </div>
          <div className="form-field">
            <label>Edition</label>
            <Select
              options={editions.map(e => ({ value: e.id, label: e.name }))}
              value={form.editionId ? { value: form.editionId, label: editions.find(e => e.id === form.editionId)?.name || '' } : null}
                  onChange={(opt) => setForm({ ...form, editionId: opt ? opt.value : '' })}
              placeholder="Search edition..."
              styles={customSelectStyles}
              isSearchable
                  isClearable
            />
          </div>
          <div className="form-field">
            <label>Position</label>
            <Select
              options={positions.map(p => ({ value: p.id, label: p.name }))}
              value={form.positionId ? { value: form.positionId, label: positions.find(p => p.id === form.positionId)?.name || '' } : null}
                  onChange={(opt) => setForm({ ...form, positionId: opt ? opt.value : '' })}
              placeholder="Search position..."
              styles={customSelectStyles}
              isSearchable
                  isClearable
            />
          </div>
        </section>

        <section className="section release-section">
          <label className="section-label">Release Dates</label>
          <div className="release-list">
            <div className="scroll-container">
            {releaseDates.map((date, idx) => (
              <div key={idx} className="release-row">
                <div className="release-input-container">
                  <DatePicker
                    selected={date.date ? new Date(date.date) : null}
                    onChange={(newDate) => {
                      const formattedDate = newDate ? 
                        `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}` : 
                        '';
                    const dates = [...releaseDates];
                      dates[idx] = { ...dates[idx], date: formattedDate };
                    setReleaseDates(dates);
                    validateDates(form.materialDueDate, dates);
                  }}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="dd/mm/yyyy"
                    className="datepicker-input"
                />
                </div>
                  {date.date && (
                    <>
                      {conflicts.blockers.some(b => b.existingRelease === date.date) ? (
                        <span className="status-pill" style={{ background: '#fee2e2', color: '#dc2626' }}>Booked</span>
                      ) : conflicts.warnings.some(w => w.existingRelease === date.date) ? (
                        <span className="status-pill" style={{ background: '#fef3c7', color: '#d97706' }}>Warning</span>
                      ) : null}
                    </>
                  )}
                <button
                  className="btn-secondary small-btn"
                  type="button"
                  onClick={() => setReleaseDates(releaseDates.filter((_, index) => index !== idx))}
                  style={{ 
                    padding: '0', 
                    borderRadius: '50%', 
                    width: '28px', 
                    height: '28px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: '#ef4444',
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                  title="Remove date"
                >
                  ×
                </button>
              </div>
            ))}
            </div>
            {conflicts.blockers.length > 0 && (
              <div style={{ marginTop: '1rem', padding: '0.8rem', borderRadius: '0.6rem', background: '#fff1f2', border: '1px solid #fda4af' }}>
                <strong style={{ color: '#be123c', display: 'block', fontSize: '0.85rem', marginBottom: '0.4rem' }}>🚫 Cannot Book - Agency Booking Conflict:</strong>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#9f1239', fontSize: '0.8rem' }}>
                  {conflicts.blockers.map((c, i) => (
                    <li key={i} style={{ marginBottom: '0.5rem' }}>
                      {c.message}
                      {isAdmin && c.canOverride && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#64748b' }}>
                          (Admin can override)
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {isAdmin && conflicts.blockers.some(b => b.canOverride) && (
                  <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #fda4af' }}>
                    <button
                      className="btn-primary"
                      onClick={() => handleSubmit(true)}
                      style={{
                        padding: '0.5rem 1rem',
                        fontSize: '0.85rem',
                        background: '#dc2626',
                        border: 'none'
                      }}
                    >
                      Override & Save (Admin Only)
                    </button>
                  </div>
                )}
              </div>
            )}
            {conflicts.warnings.length > 0 && (
              <div style={{ marginTop: '1rem', padding: '0.8rem', borderRadius: '0.6rem', background: '#fffbeb', border: '1px solid #fcd34d' }}>
                <strong style={{ color: '#854d0e', display: 'block', fontSize: '0.85rem', marginBottom: '0.4rem' }}>⚠️ Warning - Booking Conflict:</strong>
                <p style={{ color: '#713f12', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                  These positions are blocked already. You can still proceed with your booking and it will be queued.
                </p>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#713f12', fontSize: '0.8rem' }}>
                  {conflicts.warnings.map((c, i) => (
                    <li key={i} style={{ marginBottom: '0.4rem' }}>
                      {c.message}
                      {c.type === 'warning_with_edit' && c.existingBookingId && (
                        <button
                          style={{
                            marginLeft: '0.5rem',
                            padding: '0.2rem 0.5rem',
                            fontSize: '0.75rem',
                            background: '#ca8a04',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            cursor: 'pointer'
                          }}
                          onClick={async () => {
                            try {
                              const response = await api.get(`/bookings/${c.existingBookingId}`);
                              const booking = response.data.booking;
                              setEditBookingData(booking);
                              // Fetch release dates for this booking
                              if (booking.releaseDates && booking.releaseDates.length > 0) {
                                setEditReleaseDates(booking.releaseDates.map(rd => ({
                                  id: rd.id,
                                  date: rd.release_date ? rd.release_date.split('T')[0] : '',
                                  statusId: rd.status_id || ''
                                })));
                              } else {
                                setEditReleaseDates([]);
                              }
                              setEditingBooking(c.existingBookingId);
                            } catch (error) {
                              setMessage(`Error loading booking: ${error.response?.data?.message || error.message}`);
                            }
                          }}
                        >
                          Edit Previous Booking
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              className="pill-link"
              type="button"
              onClick={() => setReleaseDates([...releaseDates, { date: '' }])}
            >
              + Add Another Date
            </button>
          </div>
        </section>

        <section className="section form-grid">
          {bookingSource !== 'PUBLICATION' && (
          <div className="form-field">
            <label>Status</label>
            <Select
              options={statuses.map(s => ({ value: s.id, label: s.name }))}
              value={statuses.map(s => ({ value: s.id, label: s.name })).find(opt => opt.value === statusId)}
              onChange={(opt) => setStatusId(opt ? opt.value : '')}
              placeholder="Search status..."
              styles={customSelectStyles}
              isSearchable
              isClearable
            />
          </div>
          )}
          {bookingSource !== 'PUBLICATION' && (
          <div className="form-field">
            <label>Material Due Date</label>
              <DatePicker
                selected={form.materialDueDate ? new Date(form.materialDueDate) : null}
                onChange={(newDate) => {
                  const formattedDate = newDate ? 
                    `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}` : 
                    '';
                  setForm({ ...form, materialDueDate: formattedDate });
                  validateDates(formattedDate, releaseDates);
              }}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                className="datepicker-input"
            />
            {dateWarning && <div style={{ color: 'red', fontSize: '0.8rem', marginTop: '4px' }}>{dateWarning}</div>}
          </div>
          )}
        </section>

        <section className="section">
          <div className="form-field">
            <label>Notes</label>
            <textarea
              rows="4"
              placeholder="Add any additional notes or requirements"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className="button-row">
            <button 
              className="btn-primary" 
              type="button" 
              onClick={() => handleSubmit(false)}
              disabled={conflicts.blockers.length > 0 && !isAdmin}
              style={{ 
                opacity: (conflicts.blockers.length > 0 && !isAdmin) ? 0.5 : 1, 
                cursor: (conflicts.blockers.length > 0 && !isAdmin) ? 'not-allowed' : 'pointer' 
              }}
            >
              Save
            </button>
            <button 
              className="btn-secondary" 
              type="button" 
              onClick={() => handleSubmit(false)}
              disabled={conflicts.blockers.length > 0 && !isAdmin}
              style={{ 
                opacity: (conflicts.blockers.length > 0 && !isAdmin) ? 0.5 : 1, 
                cursor: (conflicts.blockers.length > 0 && !isAdmin) ? 'not-allowed' : 'pointer' 
              }}
            >
              Save & Add Another
            </button>
          </div>
          {message && <div className={`form-message ${conflicts.blockers.length ? 'error' : ''}`}>{message}</div>}
          
          {conflicts.warnings.length > 0 && conflicts.warnings.some(w => w.type === 'warning_with_edit') && !editingBooking && (
            <div style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: '0.8rem', background: '#fef3c7', border: '1px solid #f59e0b' }}>
              <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.5rem' }}>⚠️ Edit Previous Booking:</strong>
              <p style={{ color: '#78350f', fontSize: '0.9rem', marginBottom: '1rem' }}>
                A previous Publication booking exists. Please edit it before creating a new booking, or click "Skip & Save" to proceed without editing.
              </p>
            </div>
          )}
          
          {/* Edit Booking Modal */}
          {editingBooking && editBookingData && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setEditingBooking(null);
                setEditBookingData(null);
                setEditReleaseDates([]);
              }
            }}
            >
              <div style={{
                background: 'white',
                borderRadius: '1.25rem',
                padding: '0',
                maxWidth: '700px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}>
                {/* Modal Header */}
                <div style={{
                  padding: '1.5rem 2rem',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc'
                }}>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: '#1e293b'
                  }}>
                    Edit Previous Booking
                  </h3>
                  <button
                    onClick={() => {
                      setEditingBooking(null);
                      setEditBookingData(null);
                      setEditReleaseDates([]);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      color: '#64748b',
                      cursor: 'pointer',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#f1f5f9'}
                    onMouseLeave={(e) => e.target.style.background = 'none'}
                  >
                    ×
                  </button>
                </div>

                {/* Modal Body */}
                <div style={{
                  padding: '2rem',
                  overflowY: 'auto',
                  flex: 1
                }}>
                  <div className="form-grid" style={{ gap: '1.25rem' }}>
                    <div className="form-field">
                      <label style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#334155' }}>Client Name</label>
                      {editBookingData.booking_source === 'PUBLICATION' ? (
                        <CreatableSelect
                          options={clients.map(c => ({ value: c.id, label: c.name }))}
                          value={
                            editBookingData.client_id 
                              ? clients.map(c => ({ value: c.id, label: c.name })).find(opt => opt.value === editBookingData.client_id)
                              : editBookingData.new_client_name 
                                ? { value: 'new', label: editBookingData.new_client_name }
                                : null
                          }
                          onChange={(opt) => {
                            if (opt?.__isNew__) {
                              setEditBookingData({ ...editBookingData, client_id: null, new_client_name: opt.label });
                            } else {
                              setEditBookingData({ ...editBookingData, client_id: opt ? opt.value : null, new_client_name: '' });
                            }
                          }}
                          styles={customSelectStyles}
                          isSearchable
                          isClearable
                        />
                      ) : (
                      <Select
                        options={clients.map(c => ({ value: c.id, label: c.name }))}
                        value={clients.map(c => ({ value: c.id, label: c.name })).find(opt => opt.value === editBookingData.client_id)}
                        onChange={(opt) => setEditBookingData({ ...editBookingData, client_id: opt ? opt.value : null })}
                        styles={customSelectStyles}
                        isSearchable
                        isClearable
                      />
                      )}
                    </div>
                    <div className="form-field">
                      <label style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#334155' }}>Publication</label>
                      <Select
                        options={resources.map(r => ({ value: r.id, label: r.name }))}
                        value={resources.map(r => ({ value: r.id, label: r.name })).find(opt => opt.value === editBookingData.media_resource_id)}
                        onChange={(opt) => setEditBookingData({ ...editBookingData, media_resource_id: opt ? opt.value : null })}
                        styles={customSelectStyles}
                        isSearchable
                        isClearable
                      />
                    </div>
                    <div className="form-field">
                      <label style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#334155' }}>Edition</label>
                      <Select
                        options={editions.map(e => ({ value: e.id, label: e.name }))}
                        value={editions.map(e => ({ value: e.id, label: e.name })).find(opt => opt.value === editBookingData.edition_id)}
                        onChange={(opt) => setEditBookingData({ ...editBookingData, edition_id: opt ? opt.value : null })}
                        styles={customSelectStyles}
                        isSearchable
                        isClearable
                      />
                    </div>
                    <div className="form-field">
                      <label style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#334155' }}>Position</label>
                      <Select
                        options={positions.map(p => ({ value: p.id, label: p.name }))}
                        value={positions.map(p => ({ value: p.id, label: p.name })).find(opt => opt.value === editBookingData.position_id)}
                        onChange={(opt) => setEditBookingData({ ...editBookingData, position_id: opt ? opt.value : null })}
                        styles={customSelectStyles}
                        isSearchable
                        isClearable
                      />
                    </div>
                  </div>

                  {/* Release Dates Section */}
                  <div style={{ 
                    marginTop: '1.5rem',
                    padding: '1.5rem',
                    background: '#f8fafc',
                    borderRadius: '0.75rem',
                    border: '1px solid #e2e8f0'
                  }}>
                    <label style={{ 
                      display: 'block',
                      marginBottom: '1rem', 
                      fontSize: '0.875rem', 
                      fontWeight: 600, 
                      color: '#334155' 
                    }}>
                      Release Dates
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {editReleaseDates.map((rd, idx) => (
                        <div key={rd.id || idx} style={{ 
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          gap: '0.75rem',
                          alignItems: 'center',
                          padding: '0.75rem',
                          background: 'white',
                          borderRadius: '0.5rem',
                          border: '1px solid #e2e8f0'
                        }}>
                          <DatePicker
                            selected={rd.date ? new Date(rd.date) : null}
                            onChange={(newDate) => {
                              const formattedDate = newDate ? 
                                `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}` : 
                                '';
                              const dates = [...editReleaseDates];
                              dates[idx] = { ...dates[idx], date: formattedDate };
                              setEditReleaseDates(dates);
                            }}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            className="datepicker-input"
                          />
                          <button
                            type="button"
                            onClick={() => setEditReleaseDates(editReleaseDates.filter((_, i) => i !== idx))}
                            style={{
                              padding: '0.65rem 1rem',
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: 'none',
                              borderRadius: '0.5rem',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#fecaca';
                              e.target.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#fee2e2';
                              e.target.style.transform = 'scale(1)';
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setEditReleaseDates([...editReleaseDates, { date: '', statusId: '' }])}
                        style={{
                          padding: '0.75rem',
                          background: '#f0f4ff',
                          color: '#1c4fe0',
                          border: '1px solid #1c4fe0',
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          transition: 'all 0.2s',
                          marginTop: '0.25rem'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#e0e7ff';
                          e.target.style.borderColor = '#163eb3';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#f0f4ff';
                          e.target.style.borderColor = '#1c4fe0';
                        }}
                      >
                        + Add Release Date
                      </button>
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div className="form-field" style={{ marginTop: '1.5rem' }}>
                    <label style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#334155' }}>Notes</label>
                    <textarea
                      rows="4"
                      value={editBookingData.notes || ''}
                      onChange={(e) => setEditBookingData({ ...editBookingData, notes: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px solid #e0e5f1',
                        fontSize: '0.9rem',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </div>

                {/* Modal Footer */}
                <div style={{
                  padding: '1.5rem 2rem',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  gap: '0.75rem',
                  justifyContent: 'flex-end',
                  background: '#f8fafc'
                }}>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setEditingBooking(null);
                      setEditBookingData(null);
                      setEditReleaseDates([]);
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      fontSize: '0.9rem'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setEditingBooking(null);
                      setEditBookingData(null);
                      setEditReleaseDates([]);
                      handleSubmit(false);
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      fontSize: '0.9rem',
                      background: '#fef3c7',
                      color: '#92400e',
                      borderColor: '#f59e0b'
                    }}
                  >
                    Skip & Save New
                  </button>
                  <button
                    className="btn-primary"
                    onClick={async () => {
                      try {
                        await api.patch(`/bookings/${editingBooking}`, {
                          clientId: editBookingData.client_id,
                          mediaResourceId: editBookingData.media_resource_id,
                          editionId: editBookingData.edition_id,
                          positionId: editBookingData.position_id,
                          notes: editBookingData.notes,
                          releaseDates: editReleaseDates
                            .filter(rd => rd.date) // Only include dates that have a value
                            .map(rd => ({
                              releaseDate: rd.date,
                              statusId: editBookingData.status_id || statusId,
                              isLocked: 0,
                            })),
                        });
                        setEditingBooking(null);
                        setEditBookingData(null);
                        setEditReleaseDates([]);
                        setMessage('Previous booking updated. You can now save the new booking.');
                        // Refresh conflicts to reflect the changes
                        const checkPayload = {
                          bookingSource,
                          clientId: form.clientId,
                          mediaResourceId: form.mediaResourceId,
                          editionId: form.editionId,
                          positionId: form.positionId,
                          mediaType: form.mediaType,
                          releaseDates: releaseDates.map((rd) => ({
                            releaseDate: rd.date,
                            statusId: rd.statusId || statusId,
                          })),
                        };
                        try {
                          const response = await api.post('/bookings/check-conflicts', checkPayload);
                          setConflicts(response.data.details || { blockers: [], warnings: [] });
                        } catch (error) {
                          console.error('Error checking conflicts:', error);
                        }
                      } catch (error) {
                        setMessage(`Error updating booking: ${error.response?.data?.message || error.message}`);
                      }
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      fontSize: '0.9rem'
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

